import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssistantRuntimeProvider,
  ExportedMessageRepository,
  FeedbackAdapter,
  RuntimeAdapterProvider,
  SuggestionAdapter,
  type ThreadHistoryAdapter,
  unstable_useRemoteThreadListRuntime as useRemoteThreadListRuntime,
  useAssistantRuntime,
  useLocalThreadRuntime,
  useThreadListItem
} from "@assistant-ui/react";
import { useAgentContext } from "../context/AgentContext";
import api, {
  createThread,
  deleteThread,
  generateThreadTitle,
  getThreadMessages,
  listThreads,
  updateThread
} from "../../api/axios";
import { chatFlowController } from "./chat-flow-controller";
import {
  RemoteThreadInitializeResponse,
  RemoteThreadListResponse
} from "@assistant-ui/react/dist/runtimes/remote-thread-list/types";
import { getAccessToken } from "../../auth/tokenStorage";
import { ExportedMessageRepositoryItem } from "@assistant-ui/react/dist/runtimes/utils/MessageRepository";
import json5 from "json5";
import { parseMessageWithMentions } from "./mention-utils";
import { convertChartToMarkdown } from "../../@/components/assistant-ui/chart-utils";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { findLast } from "lodash";
import { Agent, ROLE, TYPE_RESPONSE } from "../../types";
import { Artifact } from "../../types/chat-types";
import { streamAssistantMessage } from "api/chatStream";

function extractDataObjects(streamingString: string) {
  const result = [];

  // Split using 'data: {' but keep the opening brace in each part
  const parts = streamingString.split("data: {");

  for (let i = 1; i < parts.length; i++) {
    const jsonString = "{" + parts[i]; // re-add opening brace

    try {
      const parsed = JSON.parse(jsonString.trim());
      result.push(parsed);
    } catch (err) {
      const error = err as Error;
      console.warn("Skipped invalid JSON:", jsonString, error.message);
    }
  }

  return result;
}

// Replace embedded ```chartjs{...}``` blocks inside text with markdown produced by convertChartToMarkdown
function replaceChartBlocksInText(originalText: string): string {
  if (!originalText) return "";
  const chartBlockRegex = /```chartjs([\s\S]*?)```/g;
  return originalText.replace(chartBlockRegex, (match, inner) => {
    try {
      const chartPayload = "\n\n```chartjs" + inner.trim() + "```";
      console.log("Found chart block:", chartPayload);
      const markdown = convertChartToMarkdown(chartPayload);
      return typeof markdown === "string" ? markdown : String(markdown);
    } catch (e) {
      console.warn("Failed to convert chart block, leaving as-is", e);
      return match;
    }
  });
}

function waitWithAbort(ms: number, abortSignal: AbortSignal) {
  return new Promise<void>((resolve) => {
    if (abortSignal.aborted) {
      resolve();
      return;
    }

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      abortSignal.removeEventListener("abort", cleanup);
      resolve();
    };

    const timeoutId = window.setTimeout(cleanup, ms);
    abortSignal.addEventListener("abort", cleanup, { once: true });
  });
}

async function* streamAssistantResponseFromReader(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  abortSignal: AbortSignal,
  options?: {
    shouldStop?: () => boolean;
  }
): AsyncGenerator<
  {
    content: any[];
    metadata?: {
      custom?: {
        latest_create_at?: string;
        assistant_message_id?: string;
      };
    };
  },
  void,
  unknown
> {
  const decoder = new TextDecoder();
  let buffer = "";
  let fullContent: any[] = [];
  let latestCreateAt: string | null = null;
  let assistantMessageId: string | null = null;

  const shouldStop = () => abortSignal.aborted || options?.shouldStop?.();

  const cancelReader = async () => {
    try {
      await reader.cancel();
    } catch (cancelError) {
      console.warn("Failed to cancel reader", cancelError);
    }
  };

  try {
    while (true) {
      if (shouldStop()) {
        await cancelReader();
        break;
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      if (shouldStop()) {
        await cancelReader();
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      const dataObjects = extractDataObjects(buffer);

      console.log("Received chunk:", chunk, "Extracted objects:", dataObjects);
      console.log("Processing", dataObjects.length, "data objects");

      for (const parsed of dataObjects) {
        if (shouldStop()) {
          await cancelReader();
          return;
        }

        console.log("latestCreateAt", latestCreateAt);
        console.log("Processing parsed object:", parsed);
        if (parsed.content && parsed.content.type) {
          const contentItem = parsed.content;
          console.log(
            "Content item type:",
            contentItem.type,
            "Content length:",
            contentItem.text?.length
          );

          if (
            contentItem.type === "text" ||
            contentItem.type === "final_answer" ||
            contentItem.type === "resource"
          ) {
            if (contentItem.type === "final_answer") {
              console.log(
                "Processing final_answer:",
                contentItem.text.substring(0, 100) + "..."
              );

              const existingTextIndex = fullContent.findIndex(
                (item) =>
                  item.type === "text" &&
                  !item.parentId &&
                  !item.text.includes("```chartjs")
              );

              if (existingTextIndex >= 0) {
                if (contentItem.text && contentItem.text.trim()) {
                  fullContent[existingTextIndex].text = contentItem.text;
                }
              } else {
                fullContent.push({
                  type: "text",
                  text: contentItem.text,
                });
              }

              console.log("Yielding final_answer content:", fullContent);
            } else if (contentItem.type === "resource") {
              const mermaidMarkdown = convertChartToMarkdown(contentItem.text);

              console.log("Processed chart diagram:", mermaidMarkdown);

              fullContent.push({
                type: "text",
                text: mermaidMarkdown.toString(),
              });
            } else if (contentItem.type === "text") {
              const paragraphs = contentItem.text.split(/\n\s*\n/);

              for (const paragraph of paragraphs) {
                if (!paragraph.trim()) {
                  continue;
                }

                const sentences = paragraph.split(
                  /(?<=[.!?])\s+(?=[A-ZÀÁẢÃẠÂẦẤẨẪẬĂẰẮẲẴẶĐÈÉẺẼẸÊỀẾỂỄỆÌÍỈĨỊÒÓỎÕỌÔỒỐỔỖỘƠỜỚỞỠỢÙÚỦŨỤƯỪỨỬỮỰỲÝỶỸỴ])/
                );

                for (const sentence of sentences) {
                  if (!sentence.trim()) {
                    continue;
                  }

                  const existingTextIndex = fullContent.findIndex(
                    (item) =>
                      item.type === "text" &&
                      !item.parentId &&
                      !item.text.includes("```chartjs")
                  );

                  if (existingTextIndex >= 0) {
                    const existingText = fullContent[existingTextIndex].text;
                    const needsSpace =
                      existingText.length > 0 &&
                      !existingText.endsWith(" ") &&
                      !existingText.endsWith("\n") &&
                      !sentence.startsWith(" ") &&
                      !sentence.startsWith("\n");

                    fullContent[existingTextIndex].text +=
                      (needsSpace ? " " : "") + sentence;
                  } else {
                    fullContent.push({
                      type: "text",
                      text: sentence,
                    });
                  }

                  try {
                    yield {
                      content: fullContent,
                    };
                  } catch (yieldError) {
                    console.warn(
                      "Error yielding content, continuing stream:",
                      yieldError
                    );
                  }

                  await waitWithAbort(50, abortSignal);
                  if (shouldStop()) {
                    await cancelReader();
                    return;
                  }
                }

                const existingTextIndex = fullContent.findIndex(
                  (item) =>
                    item.type === "text" &&
                    !item.parentId &&
                    !item.text.includes("```chartjs")
                );

                if (existingTextIndex >= 0) {
                  fullContent[existingTextIndex].text += "\n\n";
                }
              }
            }
          } else if (contentItem.type === "reference") {
            const referenceData = json5.parse(contentItem.text);

            const newPart: any = {
              type: "source",
              sourceType: "reference",
              id: referenceData.reference_document_id || referenceData.id,
              url: `https://example.com/document/${
                referenceData.reference_document_id || referenceData.id
              }`,
              title: JSON.stringify(referenceData),
              parentId: "reference",
            };
            fullContent.push(newPart);
          } else if (
            contentItem.type === "thinking" ||
            contentItem.type === "tool_calling"
          ) {
            console.log(
              "Processing",
              contentItem.type,
              "content:",
              contentItem.text
            );

            const existingReasoningIndex = fullContent.findIndex(
              (item) =>
                item.type === "reasoning" && item.parentId === "reasoning"
            );

            if (existingReasoningIndex >= 0) {
              const existingText = fullContent[existingReasoningIndex].text;
              const needsLineBreak =
                existingText.length > 0 && !existingText.endsWith("\n");
              fullContent[existingReasoningIndex].text +=
                (needsLineBreak ? "\n" : "") + contentItem.text;
            } else {
              fullContent.push({
                type: "reasoning",
                text: contentItem.text,
                parentId: "reasoning",
              });
            }

            const reasoningIndex = fullContent.findIndex(
              (item) =>
                item.type === "reasoning" && item.parentId === "reasoning"
            );
            if (reasoningIndex >= 0) {
              fullContent[reasoningIndex].text = fullContent[
                reasoningIndex
              ].text.replace(/\n/g, "<br>");
            }

            try {
              yield {
                content: fullContent,
              };
              console.log("Yielded reasoning content update");
            } catch (yieldError) {
              console.warn(
                "Error yielding reasoning content, continuing stream:",
                yieldError
              );
            }
          } else if (contentItem.type === "metadata") {
            console.log("Processing metadata content:", contentItem);
            try {
              const metadataObj = JSON.parse(contentItem.text);
              if (metadataObj.assistant_message_created_at) {
                latestCreateAt = metadataObj.assistant_message_created_at;
              }
              if (metadataObj.assistant_message_id) {
                assistantMessageId = metadataObj.assistant_message_id;
              }
            } catch (e) {
              console.warn(
                "Failed to parse metadata content:",
                contentItem.text,
                e
              );
            }
          } else {
            fullContent.push({
              type: "text",
              text: contentItem.text,
            });
          }

          console.log("FULL CONTENT SO FAR:", fullContent);
          console.log("latestCreateAt", latestCreateAt);
          console.log("assistantMessageId", assistantMessageId);

          try {
            yield {
              content: fullContent,
              metadata: {
                custom: {
                  ...(latestCreateAt && {
                    latest_create_at: latestCreateAt,
                  }),
                  ...(assistantMessageId && {
                    assistant_message_id: assistantMessageId,
                  }),
                },
              },
            };
          } catch (yieldError) {
            console.warn(
              "Error yielding content, continuing stream:",
              yieldError
            );
          }

          await waitWithAbort(150, abortSignal);
          if (shouldStop()) {
            await cancelReader();
            return;
          }
        }
      }

      const lastDataIndex = buffer.lastIndexOf("data: {");
      if (lastDataIndex !== -1) {
        let braceCount = 0;
        let lastCompleteIndex = -1;
        let inString = false;
        let escapeNext = false;

        for (let i = lastDataIndex; i < buffer.length; i++) {
          const char = buffer[i];

          if (escapeNext) {
            escapeNext = false;
            continue;
          }

          if (char === "\\") {
            escapeNext = true;
            continue;
          }

          if (char === '"' && !escapeNext) {
            inString = !inString;
            continue;
          }

          if (!inString) {
            if (char === "{") {
              braceCount++;
            } else if (char === "}") {
              braceCount--;
              if (braceCount === 0) {
                lastCompleteIndex = i;
                break;
              }
            }
          }
        }

        if (lastCompleteIndex !== -1) {
          buffer = buffer.substring(lastCompleteIndex + 1);
          console.log("Buffer cleared, kept incomplete chunk:", buffer);
        }
      }
    }

    if (buffer.trim()) {
      console.log("Final buffer check, remaining content:", buffer);
      const finalDataObjects = extractDataObjects(buffer);
      if (finalDataObjects.length > 0) {
        console.log("Processing final buffer objects:", finalDataObjects);
        for (const parsed of finalDataObjects) {
          if (parsed.content && parsed.content.type === "final_answer") {
            console.log("Found final_answer in final buffer check");
            const contentItem = parsed.content;

            const existingTextIndex = fullContent.findIndex(
              (item) =>
                item.type === "text" &&
                !item.parentId &&
                !item.text.includes("```chartjs")
            );

            if (existingTextIndex >= 0) {
              if (contentItem.text && contentItem.text.trim()) {
                fullContent[existingTextIndex].text = contentItem.text;
              }
            } else {
              const hasChartContent = fullContent.some(
                (item) =>
                  item.type === "text" && item.text.includes("```chartjs")
              );

              if (hasChartContent) {
                fullContent.push({
                  type: "text",
                  text: contentItem.text,
                });
              } else {
                fullContent.push({
                  type: "text",
                  text: contentItem.text,
                });
              }
            }
            console.log("latestCreateAt", latestCreateAt);
            console.log("assistantMessageId", assistantMessageId);
            try {
              yield {
                content: fullContent,
                metadata: {
                  custom: {
                    ...(latestCreateAt && {
                      latest_create_at: latestCreateAt,
                    }),
                    ...(assistantMessageId && {
                      assistant_message_id: assistantMessageId,
                    }),
                  },
                },
              };
              console.log(
                "Successfully yielded final content from buffer check"
              );
            } catch (yieldError) {
              console.warn(
                "Error yielding final content, continuing stream:",
                yieldError
              );
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// Pagination state manager for threads
class ThreadPaginationManager {
  private allThreads: any[] = [];
  private currentSkip: number = 0;
  private readonly pageSize: number = 20;
  private hasMore: boolean = true;
  private isInitialized: boolean = false;
  private currentAgentId?: string;

  reset() {
    this.allThreads = [];
    this.currentSkip = 0;
    this.hasMore = true;
    this.isInitialized = false;
    this.currentAgentId = undefined;
  }

  async loadInitial(agentId?: string): Promise<any[]> {
    // Only reset if agent changed
    if (this.currentAgentId !== agentId) {
      this.reset();
      this.currentAgentId = agentId;
    }

    // If already initialized, just return current threads
    if (this.isInitialized) {
      return this.allThreads;
    }

    this.isInitialized = true;
    return this.loadMore(agentId);
  }

  async loadMore(agentId?: string): Promise<any[]> {
    if (!this.hasMore) {
      return this.allThreads;
    }

    try {
      const response = await listThreads({
        agent_id: agentId,
        take: this.pageSize,
        skip: this.currentSkip,
      });

      // Handle new response format: { total, items }
      const total = response?.total ?? 0;
      const newThreads = response?.items ?? [];

      if (!Array.isArray(newThreads)) {
        this.hasMore = false;
        return this.allThreads;
      }

      // Filter out duplicates
      const existingIds = new Set(this.allThreads.map((t: any) => t.id));
      const uniqueNewThreads = newThreads.filter(
        (t: any) => !existingIds.has(t.id)
      );

      this.allThreads = [...this.allThreads, ...uniqueNewThreads];
      this.currentSkip += newThreads.length;

      // Check if there are more threads to load by comparing with total
      this.hasMore = this.allThreads.length < total;

      return this.allThreads;
    } catch (error) {
      console.error("Error loading threads:", error);
      this.hasMore = false;
      return this.allThreads;
    }
  }

  getHasMore(): boolean {
    return this.hasMore;
  }

  getAllThreads(): any[] {
    return this.allThreads;
  }
}

// Create a global pagination manager with refresh callback
const threadPaginationManager = new ThreadPaginationManager();
let threadListRefreshCallback: (() => void) | null = null;

// Export for use in components
export { threadPaginationManager };

// Register a callback to trigger thread list refresh
export function registerThreadListRefresh(callback: () => void) {
  threadListRefreshCallback = callback;
}

// Unregister refresh callback
export function unregisterThreadListRefresh() {
  threadListRefreshCallback = null;
}

// Trigger thread list refresh
export function triggerThreadListRefresh() {
  if (threadListRefreshCallback) {
    threadListRefreshCallback();
  }
}

// Simplified implementation using real API calls
const createDatabaseAdapter = (
  selectedAgent: any,
  navigate: NavigateFunction,
  getRuntime: () => any,
  agentId?: string
) => ({
  async list(): Promise<RemoteThreadListResponse> {
    try {
      const threads = await threadPaginationManager.loadInitial(agentId);

      return {
        threads: threads.map((t: any) => ({
          status: t.archived ? "archived" : "regular",
          remoteId: t.id,
          title: t.title,
          externalId: t.agent_id,
        })),
      };
    } catch (error) {
      console.error("Error listing threads:", error);
      return { threads: [] };
    }
  },

  async initialize(threadId: string): Promise<RemoteThreadInitializeResponse> {
    console.log(
      "myDatabaseAdapter.initialize() called with threadId:",
      threadId
    );
    try {
      console.log("Selected agent:", selectedAgent);
      if (!selectedAgent?.id) {
        throw new Error("No agent selected");
      }

      // Start message flow
      chatFlowController.initFlow();

      const thread = await createThread({
        agentId: selectedAgent.id,
        title: "New Conversation",
        archived: false,
      });

      chatFlowController.resolveThread({ thread });

      return { externalId: selectedAgent.id, remoteId: thread.id };
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },

  async rename(remoteId: string, newTitle: string): Promise<void> {
    console.log(
      "myDatabaseAdapter.rename() called with remoteId:",
      remoteId,
      "newTitle:",
      newTitle
    );
    try {
      if (remoteId && selectedAgent?.id) {
        const currentPath = window.location.pathname;
        const expectedPath = `/chat/${selectedAgent.id}/${remoteId}`;

        // Only update if we're not already on the correct URL
        if (currentPath !== expectedPath && !currentPath.includes(remoteId)) {
          console.log("Updating URL to include remoteId:", expectedPath);
          navigate(expectedPath, { replace: true });
        }
      }
      await updateThread(remoteId, { title: newTitle });
      console.log("myDatabaseAdapter.rename() - completed");
    } catch (error) {
      console.error("Error renaming thread:", error);
      throw error;
    }
  },

  async archive(remoteId: string): Promise<void> {
    console.log("myDatabaseAdapter.archive() called with remoteId:", remoteId);
    try {
      await updateThread(remoteId, { archived: true });
      console.log("myDatabaseAdapter.archive() - completed");
    } catch (error) {
      console.error("Error archiving thread:", error);
      throw error;
    }
  },

  async unarchive(remoteId: string): Promise<void> {
    console.log(
      "myDatabaseAdapter.unarchive() called with remoteId:",
      remoteId
    );
    try {
      await updateThread(remoteId, { archived: false });
      console.log("myDatabaseAdapter.unarchive() - completed");
    } catch (error) {
      console.error("Error unarchiving thread:", error);
      throw error;
    }
  },

  async delete(remoteId: string) {
    console.log("myDatabaseAdapter.delete() called with remoteId:", remoteId);
    try {
      await deleteThread(remoteId);
      console.log("myDatabaseAdapter.delete() - completed");
    } catch (error) {
      console.error("Error deleting thread:", error);
      throw error;
    }
  },

  async generateTitle(remoteId: string, messages: any[]) {
    console.log(
      "myDatabaseAdapter.generateTitle() called with remoteId:",
      remoteId,
      "messages count:",
      messages.length,
      "message",
      messages[0].content[0].text
    );
    try {
      const result = await generateThreadTitle(
        remoteId,
        messages[0].content[0].text
      );
      console.log(
        "myDatabaseAdapter.generateTitle() - generated title:",
        result.title
      );

      const runtime = getRuntime();
      console.log(runtime);
      if (runtime) {
        const run = runtime.threadList as any;
        const item = run.getItemById(remoteId);
        if (item) {
          console.log(item);
          item.rename(result.title);
        }
      }

      return new ReadableStream(); // Return empty stream as required
    } catch (error) {
      console.error("Error generating title:", error);
      throw error;
    }
  },
});

// Feedback adapter factory for collecting user feedback on messages
const createFeedbackAdapter = (
  remoteThreadId: string | undefined
): FeedbackAdapter => ({
  async submit(feedback) {
    console.log("Feedback received:", feedback);
    console.log("Remote thread ID:", remoteThreadId);
    await Promise.resolve();
  },
});

// Runtime hook component for useRemoteThreadListRuntime
function useMyRuntimeHook() {
  console.log("=== USE MY RUNTIME HOOK CALLED ===");
  return useLocalThreadRuntime(
    {
      async *run(options) {
        const data = await chatFlowController.waitForThread();
        console.log("=== CHAT MODEL ADAPTER RUN CALLED ===", data);

        const { thread } = data;
        const { messages, abortSignal } = options;
        const message = messages[messages.length - 1];

        try {
          // Parse mentions from the content array
          const parsedMessage = parseMessageWithMentions(message.content);
          
          const requestData = {
            threadId: thread.id, // Serve for message creational (in case of user message not found)
            createAt: message.createdAt.toISOString(),
          } as Record<string, any>;

          requestData.content = message.content;
          requestData.documents = parsedMessage.mentions.map(({id, name}) => ({
            id,
            name,
          }));
          console.log("Request data:", requestData);
          const response = await fetch(`${window.appConfig.apiUrl}/chat/chat`, {
            method: "POST",
            headers: {
              Accept: "text/event-stream",
              "Content-Type": "application/json",
              "Cache-Control": "no-cache",
              Authorization: `Bearer ${getAccessToken()}`,
            },
            body: JSON.stringify(requestData),
            signal: abortSignal,
          });

          if (!response.ok) {
            throw new Error(`API error: ${response.statusText}`);
          }

          // Handle streaming response using generator pattern like MockAdapter
          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error("No response body available");
          }

          const decoder = new TextDecoder();
          let buffer = "";
          let fullContent: any[] = [];
          let latestCreateAt: string | null = null;
          let assistantMessageId: string | null = null;
          let userMessageId: string | null = null;
          let suggestions: string[] = [];
          let artifact: Artifact = null;

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;

              // Use extractDataObjects to safely parse JSON without breaking on newlines in content
              const dataObjects = extractDataObjects(buffer);

              // Process each extracted object individually to create typing effect
              for (const parsed of dataObjects) {
                if (parsed?.content?.type) {
                  const contentItem = parsed.content;

                  if (
                    contentItem.type === TYPE_RESPONSE.TEXT ||
                    contentItem.type === TYPE_RESPONSE.FINAL_ANSWER ||
                    contentItem.type === TYPE_RESPONSE.RESOURCE
                  ) {
                    // For final_answer type, preserve the complete text without splitting
                    if (contentItem.type === TYPE_RESPONSE.FINAL_ANSWER) {
                      // Find existing text part that is not a chart
                      const existingTextIndex = fullContent.findIndex(
                        (item) =>
                          item.type === "text" &&
                          !item.parentId &&
                          !item.text.includes("```chartjs")
                      );

                      if (existingTextIndex >= 0) {
                        // Only replace existing text content if final_answer has content
                        // If final_answer is empty, preserve the existing text content
                        if (contentItem?.text?.trim()) {
                          fullContent[existingTextIndex].text =
                            contentItem.text;
                        }
                        // If final_answer is empty, keep the existing text content unchanged
                      } else {
                        // Create new text part if no existing content
                        fullContent.push({
                          type: "text",
                          text: contentItem.text,
                        });
                      }
                    } else if (contentItem.type === TYPE_RESPONSE.RESOURCE) {
                      const mermaidMarkdown = convertChartToMarkdown(
                        contentItem.text
                      );

                      fullContent.push({
                        type: "text",
                        text: mermaidMarkdown.toString(),
                      });
                    } else if (contentItem.type === TYPE_RESPONSE.TEXT) {
                      // For regular text chunks, use smarter splitting that preserves markdown and Vietnamese text
                      // Split by paragraphs first, then by sentences, but preserve markdown structure
                      const paragraphs = contentItem.text.split(/\n\s*\n/);

                      for (const paragraph of paragraphs) {
                        if (paragraph.trim()) {
                          // Split paragraphs into sentences more carefully
                          // Look for sentence endings that are followed by whitespace and don't break markdown
                          const sentences = paragraph.split(
                            /(?<=[.!?])\s+(?=[A-ZÀÁẢÃẠÂẦẤẨẪẬĂẰẮẲẴẶĐÈÉẺẼẸÊỀẾỂỄỆÌÍỈĨỊÒÓỎÕỌÔỒỐỔỖỘƠỜỚỞỠỢÙÚỦŨỤƯỪỨỬỮỰỲÝỶỸỴ])/
                          );

                          for (const sentence of sentences) {
                            if (sentence.trim()) {
                              // Find existing text part that is not a chart, or create new one
                              const existingTextIndex = fullContent.findIndex(
                                (item) =>
                                  item.type === "text" &&
                                  !item.parentId &&
                                  !item.text.includes("```chartjs")
                              );

                              if (existingTextIndex >= 0) {
                                // Append to existing non-chart text content, preserving line breaks
                                const existingText =
                                  fullContent[existingTextIndex].text;
                                const needsSpace =
                                  existingText.length > 0 &&
                                  !existingText.endsWith(" ") &&
                                  !existingText.endsWith("\n") &&
                                  !sentence.startsWith(" ") &&
                                  !sentence.startsWith("\n");

                                fullContent[existingTextIndex].text +=
                                  (needsSpace ? " " : "") + sentence;
                              } else {
                                // Create new text part (will not overwrite existing chart content)
                                fullContent.push({
                                  type: "text",
                                  text: sentence,
                                });
                              }

                              // Yield after each sentence for typing effect
                              try {
                                yield {
                                  content: fullContent,
                                };
                              } catch (yieldError) {
                                console.warn(
                                  "Error yielding content, continuing stream:",
                                  yieldError
                                );
                              }

                              // Add a small delay for smoother typing effect
                              await new Promise((resolve) =>
                                setTimeout(resolve, 50)
                              );
                            }
                          }

                          // Add paragraph break after each paragraph, but not to chart content
                          const existingTextIndex = fullContent.findIndex(
                            (item) =>
                              item.type === "text" &&
                              !item.parentId &&
                              !item.text.includes("```chartjs")
                          );

                          if (existingTextIndex >= 0) {
                            fullContent[existingTextIndex].text += "\n\n";
                          }
                        }
                      }
                    }
                  } else if (contentItem.type === TYPE_RESPONSE.REFERENCE) {
                    const referenceData = json5.parse(contentItem.text);

                    // Convert reference to source format
                    const newPart: any = {
                      type: "source",
                      sourceType: "reference",
                      id:
                        referenceData.reference_document_id || referenceData.id,
                      url: `https://example.com/document/${
                        referenceData.reference_document_id || referenceData.id
                      }`,
                      title: JSON.stringify(referenceData),
                      parentId: "reference",
                    };
                    fullContent.push(newPart);
                  } else if (
                    contentItem.type === TYPE_RESPONSE.THINKING ||
                    contentItem.type === TYPE_RESPONSE.TOOL_CALLING
                  ) {
                    // Find existing reasoning part or create new one
                    const existingReasoningIndex = fullContent.findIndex(
                      (item) =>
                        item.type === "reasoning" &&
                        item.parentId === "reasoning"
                    );

                    if (existingReasoningIndex >= 0) {
                      // Append to existing reasoning content with proper line breaks
                      const existingText =
                        fullContent[existingReasoningIndex].text;
                      const needsLineBreak =
                        existingText.length > 0 && !existingText.endsWith("\n");
                      fullContent[existingReasoningIndex].text +=
                        (needsLineBreak ? "\n" : "") + contentItem.text;
                      console.log(
                        "Updated reasoning content:",
                        fullContent[existingReasoningIndex].text
                      );
                    } else {
                      // Create new reasoning part
                      fullContent.push({
                        type: "reasoning",
                        text: contentItem.text,
                        parentId: "reasoning",
                      });
                      console.log(
                        "Created new reasoning part:",
                        contentItem.text
                      );
                    }

                    // Convert \n to <br> tags for HTML rendering
                    // Note: This assumes the UI component can render HTML. If not, the UI component
                    // should apply CSS: white-space: pre-wrap; to preserve line breaks
                    const reasoningIndex = fullContent.findIndex(
                      (item) =>
                        item.type === "reasoning" &&
                        item.parentId === "reasoning"
                    );
                    if (reasoningIndex >= 0) {
                      fullContent[reasoningIndex].text = fullContent[
                        reasoningIndex
                      ].text.replace(/\n/g, "<br>");
                    }

                    // Yield the updated content immediately for reasoning
                    try {
                      yield {
                        content: fullContent,
                      };
                      console.log("Yielded reasoning content update");
                    } catch (yieldError) {
                      console.warn(
                        "Error yielding reasoning content, continuing stream:",
                        yieldError
                      );
                    }
                  } else if (contentItem.type === TYPE_RESPONSE.METADATA) {
                    try {
                      const metadataObj = JSON.parse(contentItem.text);
                      if (metadataObj.assistant_message_created_at) {
                        console.log(metadataObj);
                        latestCreateAt =
                          metadataObj.assistant_message_created_at;
                      }
                      if (metadataObj.assistant_message_id) {
                        assistantMessageId = metadataObj.assistant_message_id;
                      }
                      if (metadataObj.user_message_id) {
                        userMessageId = metadataObj.user_message_id;
                      }
                    } catch (e) {
                      console.warn(
                        "Failed to parse metadata content:",
                        contentItem.text,
                        e
                      );
                    }
                  } else if (contentItem.type === TYPE_RESPONSE.SUGGESTION) {
                    suggestions = contentItem.text;
                  } else if (contentItem.type === TYPE_RESPONSE.ARTIFACT) {
                    artifact = contentItem;
                  } else {
                    // For any other content type, add as new part
                    fullContent.push({
                      type: "text",
                      text: contentItem.text,
                    });
                  }

                  // Yield the accumulated content after each individual object (except for text which is handled above)
                  try {
                    yield {
                      content: fullContent,
                      metadata: {
                        custom: {
                          ...(latestCreateAt && {
                            latest_create_at: latestCreateAt,
                          }),
                          ...(assistantMessageId && {
                            assistant_message_id: assistantMessageId,
                          }),
                          ...(userMessageId && {
                            user_message_id: userMessageId, // Add this line
                          }),
                          ...(suggestions.length > 0 && {
                            suggestions: suggestions,
                          }),
                          ...(artifact && {
                            artifact: artifact,
                          }),
                        },
                      },
                    };
                  } catch (yieldError) {
                    console.warn(
                      "Error yielding content, continuing stream:",
                      yieldError
                    );
                  }

                  // Add a small delay for smoother typing effect
                  await new Promise((resolve) => setTimeout(resolve, 150));
                }
              }

              // Clear the buffer after processing to avoid reprocessing the same data
              // Keep only the last incomplete chunk if any
              const lastDataIndex = buffer.lastIndexOf("data: {");
              if (lastDataIndex !== -1) {
                // Look for the last complete JSON object by finding balanced braces
                let braceCount = 0;
                let lastCompleteIndex = -1;
                let inString = false;
                let escapeNext = false;

                for (let i = lastDataIndex; i < buffer.length; i++) {
                  const char = buffer[i];

                  if (escapeNext) {
                    escapeNext = false;
                    continue;
                  }

                  if (char === "\\") {
                    escapeNext = true;
                    continue;
                  }

                  if (char === '"' && !escapeNext) {
                    inString = !inString;
                    continue;
                  }

                  if (!inString) {
                    if (char === "{") {
                      braceCount++;
                    } else if (char === "}") {
                      braceCount--;
                      if (braceCount === 0) {
                        lastCompleteIndex = i;
                        break;
                      }
                    }
                  }
                }

                if (lastCompleteIndex !== -1) {
                  // Keep everything after the last complete JSON object
                  buffer = buffer.substring(lastCompleteIndex + 1);
                  console.log("Buffer cleared, kept incomplete chunk:", buffer);
                }
              }
            }

            // Final check: if we have any remaining buffer content, try to process it
            if (buffer.trim()) {
              console.log("Final buffer check, remaining content:", buffer);
              const finalDataObjects = extractDataObjects(buffer);
              if (finalDataObjects.length > 0) {
                console.log(
                  "Processing final buffer objects:",
                  finalDataObjects
                );
                for (const parsed of finalDataObjects) {
                  if (
                    parsed.content &&
                    parsed.content.type === "final_answer"
                  ) {
                    console.log("Found final_answer in final buffer check");
                    const contentItem = parsed.content;

                    // Find existing text part that is not a chart
                    const existingTextIndex = fullContent.findIndex(
                      (item) =>
                        item.type === "text" &&
                        !item.parentId &&
                        !item.text.includes("```chartjs")
                    );

                    if (existingTextIndex >= 0) {
                      // Only replace existing text content if final_answer has content
                      // If final_answer is empty, preserve the existing text content
                      if (contentItem.text && contentItem.text.trim()) {
                        fullContent[existingTextIndex].text = contentItem.text;
                      }
                      // If final_answer is empty, keep the existing text content unchanged
                    } else {
                      // Check if we have chart content that we need to preserve
                      const hasChartContent = fullContent.some(
                        (item) =>
                          item.type === "text" &&
                          item.text.includes("```chartjs")
                      );

                      if (hasChartContent) {
                        // If we have chart content, append the final answer as a new text part
                        fullContent.push({
                          type: "text",
                          text: contentItem.text,
                        });
                      } else {
                        // Create new text part if no existing content
                        fullContent.push({
                          type: "text",
                          text: contentItem.text,
                        });
                      }
                    }
                    console.log("latestCreateAt", latestCreateAt);
                    console.log("assistantMessageId", assistantMessageId);
                    // Yield the final complete content
                    try {
                      yield {
                        content: fullContent,
                        metadata: {
                          custom: {
                            ...(latestCreateAt && {
                              latest_create_at: latestCreateAt,
                            }),
                            ...(assistantMessageId && {
                              assistant_message_id: assistantMessageId,
                            }),
                            ...(suggestions.length > 0 && {
                              suggestions: suggestions,
                            }),
                          },
                        },
                      };
                      console.log(
                        "Successfully yielded final content from buffer check"
                      );
                    } catch (yieldError) {
                      console.warn(
                        "Error yielding final content, continuing stream:",
                        yieldError
                      );
                    }
                  }
                }
              }
            }
          } catch (streamError) {
            console.warn(
              "Error in stream processing, but continuing:",
              streamError
            );
            // Don't throw the error, just log it and continue
          } finally {
            reader.releaseLock();
          }
        } catch (error) {
          // Don't throw the error to prevent detach, just log it
          // This prevents the {detach: true} error that cancels the stream
          if (error && typeof error === "object" && "detach" in error) {
            console.warn(
              "Stream detach detected, this is likely due to React rendering issues"
            );
            return; // Gracefully end the stream
          }

          // For other errors, log them but don't throw to prevent stream cancellation
          console.warn("Error in chat stream (continuing):", error);

          // Check if this is a React rendering error
          if (
            error &&
            typeof error === "object" &&
            "message" in error &&
            typeof (error as any).message === "string" &&
            (error as any).message.includes(
              "Objects are not valid as a React child"
            )
          ) {
            console.warn(
              "React rendering error detected, continuing stream gracefully"
            );
            return; // Gracefully end the stream instead of throwing
          }

          // For any other error, don't throw to prevent stream cancellation
          console.warn(
            "Non-critical error in chat stream, ending gracefully:",
            error
          );
          return; // Gracefully end the stream instead of throwing
        }
      },
    },
    {}
  );
}

// Thread-specific provider component
function ThreadSpecificProvider({
  children,
}: Readonly<{ children?: React.ReactNode }>) {
  console.log("=== THREAD SPECIFIC PROVIDER RENDERED ===");
  // This runs in the context of each thread
  const threadListItem = useThreadListItem();
  const assistantRuntime = useAssistantRuntime();
  const demoThreadRef = useRef<string | null>(null);
  const streamingCleanupRef = useRef<(() => void) | null>(null);
  const _remoteThreadId = useRef<string | undefined>();
  const [pendingStreamThreadId, setPendingStreamThreadId] = useState<
    string | null
  >(null);
  console.log("ThreadSpecificProvider - threadListItem:", threadListItem);

  useEffect(() => {
    _remoteThreadId.current = threadListItem.remoteId;
  }, [threadListItem]);

  const startStreamingForThread = useCallback(
    (remoteId?: string) => {
      if (!assistantRuntime) {
        streamingCleanupRef.current?.();
        return;
      }

      if (!remoteId) {
        streamingCleanupRef.current?.();
        return;
      }

      streamingCleanupRef.current?.();

      let threadRuntime: any = null;
      try {
        threadRuntime = assistantRuntime.threadList.getById(remoteId);
      } catch (error) {
        console.warn("Failed to access thread runtime", error);
        return;
      }

      if (!threadRuntime) {
        return;
      }

      let cancelled = false;
      let unsubscribe: (() => void) | undefined;

      const cleanup = () => {
        if (cancelled) {
          return;
        }
        cancelled = true;
        unsubscribe?.();
        streamingCleanupRef.current = null;
        if (demoThreadRef.current === remoteId) {
          demoThreadRef.current = null;
        }
      };

      const tryStartStreaming = () => {
        if (cancelled || demoThreadRef.current === remoteId) {
          return;
        }

        const state = threadRuntime.getState();
        if (state.isLoading) {
          return;
        }

        const lastUserMessage = [...(state.messages ?? [])]
          .reverse()
          .find((message: any) => message.role === "user");
        if (!lastUserMessage) {
          return;
        }

        demoThreadRef.current = remoteId;
        streamingCleanupRef.current = cleanup;

        threadRuntime.unstable_resumeRun({
          parentId: lastUserMessage.id ?? null,
          stream: async function* ({
            abortSignal,
          }: {
            abortSignal: AbortSignal;
          }) {
            const metadata =
              (lastUserMessage.metadata as any) ??
              (lastUserMessage.message?.metadata as any);
            const userMessageId =
              metadata?.custom?.assistant_message_id ?? lastUserMessage.id;

            if (!userMessageId) {
              console.warn("No user message id found for streaming");
              cleanup();
              return;
            }

            const shouldStop = () => cancelled || abortSignal.aborted;

            try {
              const reader = await streamAssistantMessage(userMessageId, {
                signal: abortSignal,
              });

              try {
                let lastUpdate: any = null;

                for await (const update of streamAssistantResponseFromReader(
                  reader,
                  abortSignal,
                  { shouldStop }
                )) {
                  if (shouldStop()) {
                    break;
                  }

                  yield {
                    content: update.content,
                    ...(update.metadata && { metadata: update.metadata }),
                    status: { type: "running" },
                  } as any;

                  lastUpdate = update;
                }

                if (!shouldStop()) {
                  yield {
                    content: lastUpdate?.content ?? [],
                    ...(lastUpdate?.metadata && {
                      metadata: lastUpdate.metadata,
                    }),
                    status: { type: "complete", reason: "unknown" },
                  } as any;
                }
              } finally {
                await reader.cancel().catch((): undefined => undefined);
              }
            } catch (error) {
              if (shouldStop()) {
                console.log("Stream cancelled", error);
              } else {
                console.warn("Assistant stream failed", error);
              }
            } finally {
              cleanup();
            }
          },
        });
      };

      unsubscribe = threadRuntime.subscribe(tryStartStreaming);
      streamingCleanupRef.current = cleanup;
      tryStartStreaming();

      return cleanup;
    },
    [assistantRuntime]
  );

  // Create thread-specific history adapter using the same pattern as existing threadHistoryAdapter
  const history = useMemo<ThreadHistoryAdapter>(
    () => ({
      async load(): Promise<
        ExportedMessageRepository & { unstable_resume?: boolean }
      > {
        const remoteId = threadListItem.remoteId;
        if (!remoteId) {
          return { messages: [], unstable_resume: false };
        }
        try {
          // Load messages from API
          const resp = await getThreadMessages(remoteId);
          if (resp.status !== 200) {
            throw new Error("Failed to load messages");
          }

          const messages = resp.data;
          console.log("Loaded messages:", messages);

          chatFlowController.initFlow();
          chatFlowController.resolveThread({ thread: { id: remoteId } });

          // [messages[0], messages[1]] = [messages[1], messages[0]];

          const reversedMessages = [...messages].reverse();

          // Check if thread is currently streaming
          let shouldStartStreaming = false;
          const threadResp = await api.get(`/chat/threads/${remoteId}`);
          if (threadResp.status !== 200) {
          } else {
            shouldStartStreaming = threadResp.data.is_streaming;
          }

          console.log("Reversed messages:", reversedMessages);
          // Transform to the expected format
          const transformedMessages = reversedMessages.map((m: any) => {
            if (m.references) {
              console.log(m.references);

              const internalReferences = m.references.map(
                (ref: any, index: number) => ({
                  documentName: ref.name,
                  title: JSON.stringify([m.references[index]]),
                  // Pass along other useful data for click handlers
                  id: ref.id,
                  url: ref.path,
                  sourceType: ref.metadata.file_type,
                  type: "reference",
                  parentId: "reference",
                })
              );

              m.content.splice(m.content.length - 1, 0, ...internalReferences);
            }
            return {
              message: {
                role: m.role.toLowerCase(),
                content: Array.isArray(m.content)
                  ? m.content.map((part: any) => {
                      console.log(part);
                      let type = part.type || "text";
                      if (type === "thinking" || type === "tool_calling") {
                        type = "reasoning";
                        return {
                          ...part,
                          type,
                          parentId: type,
                        };
                      }
                      if (type === "reference") {
                        type = "source";
                        return {
                          ...part,
                          type,
                        };
                      }
                      if (type === "resource") {
                        // Convert chart content to text with markdown formatting for history loading
                        const finalResult = convertChartToMarkdown(part.text);
                        return {
                          type: "text",
                          text: finalResult,
                        };
                      }
                      if (type === "text") {
                        // Detect and convert embedded chart blocks inside text
                        const processedText = replaceChartBlocksInText(
                          part.text || ""
                        );
                        console.log("Processed text part:", processedText);
                        return {
                          type: "text",
                          text: processedText,
                        };
                      }
                      if (
                        type === "metadata" ||
                        type === TYPE_RESPONSE.ARTIFACT
                      ) {
                        return {
                          type: "text",
                          text: "",
                        };
                      }
                      return {
                        type,
                        text: part.text || "",
                      };
                    })
                  : [
                      {
                        type:
                          m.content?.type === "thinking"
                            ? "reasoning"
                            : m.content?.type === "reference"
                            ? "source"
                            : "text",
                        text: replaceChartBlocksInText(
                          String(m.content?.text ?? m.content)
                        ),
                      },
                    ],
                id: m.id,
                createdAt: new Date(m.createdAt),
                ...(m.parentId && { parentId: m.parentId }),
                status: {
                  type: "complete",
                },
                metadata: {
                  custom: {
                    artifact: m.content?.find(
                      (ele: { type: string; artifact: string }) =>
                        ele.type === TYPE_RESPONSE.ARTIFACT
                    ),
                  },
                },
                // Preserve feedback information from API
                ...(m.is_like !== undefined && { is_like: m.is_like }),
                ...(m.is_dislike !== undefined && {
                  is_dislike: m.is_dislike,
                }),
              },
              parentId: m.parentId ?? null,
            };
          }) as any;

          let finalMessages = transformedMessages;
          if (shouldStartStreaming && transformedMessages.length > 0) {
            finalMessages = transformedMessages.slice(0, -1);
            setPendingStreamThreadId(remoteId);
          }
          return {
            messages: finalMessages,
            unstable_resume: finalMessages.length > 0,
          };
        } catch (error) {
          console.error("Error loading messages:", error);
          return { messages: [], unstable_resume: false };
        }
      },

      async append(item: ExportedMessageRepositoryItem): Promise<void> {
        // const remoteId = _remoteThreadId.current;
        // if (remoteId) {
        //   chatFlowController.initFlow();
        //   chatFlowController.resolveThread({
        //     thread: { id: remoteId },
        //   });
        // } else {
        //   const { thread } = await chatFlowController.waitForThread();
        //   chatFlowController.resolveUserMessage({ thread });
        //   return;
        // }
        // if (item.message.role === "assistant") {
        //   // do no thing, assistant message was handled in Backend
        // } else {
        //   const { thread } = await chatFlowController.waitForThread();
        //   const messageData = {
        //     role: item.message.role,
        //     content: Array.isArray(item.message.content)
        //       ? item.message.content
        //       : [{ type: "text", text: String(item.message.content) }],
        //     createdAt: item.message.createdAt.toISOString(),
        //     ...(item.message.metadata && { metadata: item.message.metadata }),
        //   };
        //   const resp = await sendMessage(thread.id, messageData);
        //   if (resp.status !== 200) {
        //     throw new Error("Failed to send message");
        //   }
        //   // chatFlowController.resolveUserMessage({
        //   //   thread,
        //   //   userMessage: resp.data.message,
        //   // });
        // }
      },
    }),
    [startStreamingForThread]
  );

  const createSuggestionAdapter = (): SuggestionAdapter => ({
    async generate({ messages }) {
      const lastMessage = findLast(
        messages,
        (ele) => ele.role === ROLE.ASSISTANT
      );
      const suggestionsFromMetadata =
        lastMessage?.metadata?.custom?.suggestions;

      if (Array.isArray(suggestionsFromMetadata)) {
        return suggestionsFromMetadata?.map((s) => {
          if (typeof s === "string") {
            return { prompt: s };
          }
          return { prompt: s.prompt || s.title || "" };
        });
      }

      return [];
    },
  });

  const adapters = useMemo(
    () => ({
      history,
      feedback: createFeedbackAdapter(_remoteThreadId.current),
      suggestion: createSuggestionAdapter(),
    }),
    [history, _remoteThreadId.current]
  );

  useEffect(() => {
    if (!pendingStreamThreadId) {
      return;
    }

    const remoteId = pendingStreamThreadId;
    setPendingStreamThreadId(null);
    startStreamingForThread(remoteId);
  }, [pendingStreamThreadId, startStreamingForThread]);

  return (
    <RuntimeAdapterProvider adapters={adapters}>
      {children}
    </RuntimeAdapterProvider>
  );
}

function RuntimeProviderInner({
  children,
  initialThreadId,
  currentAgentId,
  selectedAgent,
  navigate,
  isLogsAgentPage,
}: Readonly<{
  children: React.ReactNode;
  initialThreadId?: string;
  currentAgentId?: string;
  selectedAgent: Agent;
  navigate: NavigateFunction;
  isLogsAgentPage: boolean;
}>) {
  const runtimeRef = useRef<any>(null);
  const hasSwitchedToFirstThreadRef = useRef<boolean>(false);

  const myDatabaseAdapter = useMemo(
    () =>
      createDatabaseAdapter(
        selectedAgent,
        navigate,
        () => runtimeRef.current,
        currentAgentId
      ),
    [selectedAgent, navigate, currentAgentId]
  );

  const runtime = useRemoteThreadListRuntime({
    runtimeHook: useMyRuntimeHook,
    adapter: {
      ...myDatabaseAdapter,
      // The Provider component adds thread-specific adapters
      unstable_Provider: ThreadSpecificProvider,
    },
  });

  useEffect(() => {
    if (runtime) {
      runtimeRef.current = runtime;
    }
  }, [runtime]);

  useEffect(() => {
    if (!isLogsAgentPage) {
      hasSwitchedToFirstThreadRef.current = false;
    }
  }, [isLogsAgentPage]);

  useEffect(() => {
    if (!runtime) return;
    const unsubscribe = runtime.threads.subscribe(() => {
      const threadList = runtime.threads.getState();
      const existingThreadId = threadList?.threads?.some(
        (thread) => thread === initialThreadId
      );

      if (
        isLogsAgentPage &&
        threadList?.threads?.length > 0 &&
        !hasSwitchedToFirstThreadRef.current
      ) {
        runtime.switchToThread(threadList?.threads?.[0]);
        hasSwitchedToFirstThreadRef.current = true;
      } else if (
        initialThreadId &&
        threadList.threads.length > 0 &&
        existingThreadId
      ) {
        runtime.switchToThread(initialThreadId);
      }
    });

    return () => unsubscribe();
  }, [initialThreadId, runtime, isLogsAgentPage]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}

export function MyRuntimeProvider({
  children,
  initialThreadId,
  isLogsAgentPage,
}: Readonly<{
  children: React.ReactNode;
  initialThreadId?: string;
  isLogsAgentPage?: boolean;
}>) {
  const navigate = useNavigate();
  const { id, agentId } = useParams();
  const { selectedAgent } = useAgentContext();
  const currentAgentId = isLogsAgentPage ? id : selectedAgent?.id ?? agentId;

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Register global refresh callback
  useEffect(() => {
    registerThreadListRefresh(() => {
      setRefreshTrigger((prev) => prev + 1);
    });

    return () => {
      unregisterThreadListRefresh();
    };
  }, []);

  return (
    <RuntimeProviderInner
      key={`${currentAgentId || "default"}-${refreshTrigger}`}
      initialThreadId={initialThreadId}
      currentAgentId={currentAgentId}
      selectedAgent={selectedAgent}
      navigate={navigate}
      isLogsAgentPage={isLogsAgentPage}
    >
      {children}
    </RuntimeProviderInner>
  );
}
